import React from "react";
import { motion } from "framer-motion";

const SCREENSHOTS = [
  "/assets/feedback-1.png",
  "/assets/feedback-2.png",
  "/assets/feedback-3.png",
  "/assets/feedback-4.png",
  "/assets/feedback-5.png",
];

const CHIPS = [
  { label: "Real Results", dot: true },
  { label: "Live Members", dot: true },
  { label: "Verified Signals", dot: true },
];

const PhoneCard = ({ src, i }) => (
  <div
    className="relative shrink-0 group"
    style={{ width: "clamp(220px, 28vw, 300px)" }}
    data-testid={`feedback-card-${i}`}
  >
    {/* Ambient glow */}
    <div className="pointer-events-none absolute -inset-6 rounded-[44px] bg-[radial-gradient(ellipse_at_center,rgba(78,168,255,0.18),transparent_65%)] blur-2xl opacity-70 transition-opacity duration-700 group-hover:opacity-100" />

    {/* Glass phone frame */}
    <div className="relative rounded-[28px] p-[6px] glass-panel overflow-hidden">
      {/* Subtle inner glow */}
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/[0.06]" />
      {/* Reflection sweep */}
      <div className="pointer-events-none absolute inset-0 rounded-[28px] overflow-hidden">
        <div className="anim-sheen absolute inset-0" />
      </div>

      {/* Screenshot — full mobile, no crop, no tilt */}
      <div className="relative rounded-[22px] overflow-hidden bg-[#04060a]">
        <img
          src={src}
          alt={`BIGBULL trader feedback ${i + 1}`}
          loading="lazy"
          className="block w-full h-auto select-none pointer-events-none"
        />
        {/* Bottom fade for premium feel */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#04060a]/40 to-transparent" />
      </div>
    </div>

    {/* Mini meta label below card */}
    <div className="mt-4 flex items-center justify-center gap-2">
      <span className="h-1 w-1 rounded-full bg-[#4ea8ff] shadow-[0_0_8px_#4ea8ff]" />
      <span className="text-[10px] tracking-[0.3em] uppercase text-white/40">
        Verified Member · Telegram
      </span>
    </div>
  </div>
);

const Testimonials = () => {
  // Duplicate set for seamless marquee
  const loop = [...SCREENSHOTS, ...SCREENSHOTS];

  return (
    <section
      id="results"
      className="relative pt-6 sm:pt-10 md:pt-14 pb-20 md:pb-28 overflow-hidden"
      data-testid="testimonials-section"
    >
      {/* Ambient background accents */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="glow-blob top-[20%] left-[-8%] h-[420px] w-[420px] bg-[rgba(31,111,224,0.10)]" />
        <div className="glow-blob bottom-[10%] right-[-8%] h-[460px] w-[460px] bg-[rgba(78,168,255,0.08)]" />
      </div>

      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center text-center max-w-[780px] mx-auto"
        >
          {/* Chips */}
          <div className="flex flex-wrap items-center justify-center gap-2.5 mb-7">
            {CHIPS.map((c) => (
              <span
                key={c.label}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase text-white/65 backdrop-blur-md"
              >
                {c.dot && (
                  <span className="h-1 w-1 rounded-full bg-[#4ea8ff] shadow-[0_0_8px_#4ea8ff]" />
                )}
                {c.label}
              </span>
            ))}
          </div>

          <h2 className="font-display text-[36px] sm:text-[48px] md:text-[60px] leading-[1.02] tracking-[-0.03em] text-balance">
            <span className="text-gradient-silver">Trusted by</span>{" "}
            <span className="text-gradient-blue">real traders.</span>
          </h2>
          <p className="mt-5 text-[14px] sm:text-[15.5px] md:text-[17px] text-white/55 font-light max-w-[620px]">
            Thousands of binary traders use BIGBULL AI BOT daily for structured
            Quotex signals and precision OTC entries.
          </p>
        </motion.div>
      </div>

      {/* Auto-slider marquee */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: [0.2, 0.7, 0.2, 1] }}
        className="relative mt-14 md:mt-20"
      >
        <div className="testimonial-marquee marquee-fade-x">
          <div className="testimonial-track">
            {loop.map((src, i) => (
              <PhoneCard src={src} i={i} key={`${src}-${i}`} />
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default Testimonials;
